import {Trans} from '@lingui/macro';
import React, {FC} from 'react';
import styled, {css} from 'styled-components';

import {Handler} from '../../../../../libs/shared/helpers/src/react/reactHelpers';
import {IconButton} from '../../../../../libs/shared/ui/src/components/buttons/iconButton';
import {useUserTheme} from '../../../../../libs/shared/ui/src/theme/userThemeProvider';
import {isJobRunning} from '../../../../helpers/jobHelpers';
import {AccountModel} from '../../../../models/api/accountModel';
import {AccountDomainAssociationJobModel} from '../../../../models/api/jobModel';

/*
 * Props.
 */

interface EmailDomainDeleteButtonProps {
  account: AccountModel;
  jobs: ReadonlyArray<AccountDomainAssociationJobModel>;
  onClick: Handler;
}

/*
 * Styles.
 */

const StyledIconButton = styled(IconButton)<{isDisabled?: boolean}>`
  ${(p) =>
    p.isDisabled &&
    css`
      cursor: not-allowed;
    `}
`;

/*
 * Component.
 */

export const EmailDomainDeleteButton: FC<EmailDomainDeleteButtonProps> = (props) => {
  const theme = useUserTheme();
  const {account, jobs, onClick} = props;
  const isDisabled = computeIsConflict(jobs, account);

  return (
    <StyledIconButton
      iconName="trashSmall"
      color={theme.greys.shade70}
      label={renderRemoveLabel(jobs, account)}
      onClick={onClick}
      isDisabled={isDisabled} // Applies the style which changes the cursor type, and tells the IconButton to set isDisabled on its Icon
    />
  );
};

/*
 * Helpers.
 */

function computeIsConflict(
  jobs: ReadonlyArray<AccountDomainAssociationJobModel>,
  account: AccountModel,
): boolean {
  // Filter down to Jobs which are active and on the currently selected Account
  const conflictingJobs = jobs.filter((job) => job.account_id === account.id).filter(isJobRunning);
  return conflictingJobs.length > 0;
}

function renderRemoveLabel(jobs: ReadonlyArray<AccountDomainAssociationJobModel>, account: AccountModel) {
  if (computeIsConflict(jobs, account)) {
    return <Trans id="account-details-email-domains-remove-busy">Associating contacts, please wait...</Trans>;
  }

  return <Trans id="account-details-email-domains-remove">Remove</Trans>;
}
